/**
 * Root app router. Stop wizard lives at /admin/stops/new and /admin/stops/:stopId/edit
 * (declared before /admin/stops so the list route does not capture those paths).
 */
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuthSimple';
import { FeatureFlagsProvider } from './hooks/useFeatureFlags.jsx';
import ProtectedRoute from './components/ProtectedRouteSimple';
import PublicRoute from './components/PublicRoute';
import SubscriptionRoute from './components/SubscriptionRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import SuperadminDashboard from './pages/frontSuperadmin';
import SuperadminPlatform from './pages/frontSuperadminPlatform';
import Layout from './components/Layout';
import LoginPage from './pages/frontLogin';
import ResetPassword from './pages/frontResetPassword';
import ForgotPasswordPage from './pages/frontForgotPassword';
import AppRedirectPage from './pages/AppRedirectPage';
import Dashboard from './pages/frontDashboard';
import UnifiedTracking from './pages/frontGpsSuivi';
import FleetMonitor from './pages/frontFleetMonitor';
import History from './pages/frontHistory';
import BusManagement from './pages/frontBuses';
import DriverManagement from './pages/frontDrivers';
import StopManagement from './pages/frontStops';
import StopWizardPage from './pages/frontStopWizard';
import StudentManagementEnhanced from './pages/frontStudents';
import ParentManagement from './pages/frontParents';
import AdminSettings from './pages/frontSettings';
import NotificationsEnhanced from './pages/frontNotifications';
import BroadcastComposer from './pages/frontBroadcast';
import SentNotifications from './pages/frontSentNotifications';
import AssignmentManagement from './pages/frontAssignments';
import LandingPage from './pages/LandingPage';
import FAQ from './pages/FAQ';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RequestsManagementPremium from './pages/frontRequests';
import IncidentsManagementPremium from './pages/frontIncidents';
import DevSimulator from './pages/frontDevSimulator';
import { DevSimulatorProvider } from './context/DevSimulatorContext';
import { Toaster } from './components/ui/sonner';
import { useAnalytics } from './hooks/useAnalytics';

const IS_DEV_MODE =
  import.meta.env.VITE_DEV_MODE === 'false' ? false : import.meta.env.VITE_DEV_MODE === 'true' || import.meta.env.DEV;

// Wrapper conditionnel : monte le Provider uniquement en dev mode.
// Placé au-dessus du Router pour que l'état survive à la navigation.
const MaybeDevProvider = ({ children }) =>
  IS_DEV_MODE ? <DevSimulatorProvider>{children}</DevSimulatorProvider> : <>{children}</>;

function AnalyticsTracker() {
  useAnalytics();
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <FeatureFlagsProvider subscription="basic">
          <MaybeDevProvider>
            <Router>
              <AnalyticsTracker />
              <Routes>
                {/* Routes publiques */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/app/reset-password" element={<AppRedirectPage action="reset-password" />} />

                {/* Routes superadmin — accès cross-école */}
                <Route
                  path="/superadmin"
                  element={
                    <SuperAdminRoute>
                      <SuperadminDashboard />
                    </SuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/platform"
                  element={
                    <SuperAdminRoute>
                      <SuperadminPlatform />
                    </SuperAdminRoute>
                  }
                />

                {/* Routes protégées avec layout */}
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Navigate to="/admin/tracking" replace />} />

                          {/* Route protégée par abonnement — Premium uniquement */}
                          <Route
                            path="/dashboard"
                            element={
                              <SubscriptionRoute page="dashboard" requiredSubscription="premium">
                                <Dashboard />
                              </SubscriptionRoute>
                            }
                          />

                          {/* Page unifiée de suivi GPS et temps réel */}
                          <Route path="/tracking" element={<UnifiedTracking />} />
                          {/* Suivi flotte 24/7 */}
                          <Route path="/fleet" element={<FleetMonitor />} />
                          <Route
                            path="/history"
                            element={
                              <SubscriptionRoute page="gps-tracking" requiredSubscription="premium">
                                <History />
                              </SubscriptionRoute>
                            }
                          />

                          {/* Redirections pour compatibilité */}
                          <Route path="/real-time-tracking" element={<Navigate to="/tracking" replace />} />
                          <Route path="/gps-tracking" element={<Navigate to="/tracking" replace />} />

                          <Route path="/buses" element={<BusManagement />} />
                          <Route path="/drivers" element={<DriverManagement />} />
                          <Route path="/stops/new" element={<StopWizardPage />} />
                          <Route path="/stops/:stopId/edit" element={<StopWizardPage />} />
                          <Route path="/stops" element={<StopManagement />} />
                          <Route path="/students" element={<StudentManagementEnhanced />} />
                          <Route path="/parents" element={<ParentManagement />} />
                          <Route path="/assignments" element={<AssignmentManagement />} />

                          {/* Route protégée par abonnement — Premium uniquement */}
                          <Route
                            path="/notifications"
                            element={
                              <SubscriptionRoute page="notifications" requiredSubscription="premium">
                                <NotificationsEnhanced />
                              </SubscriptionRoute>
                            }
                          />
                          <Route
                            path="/notifications/new"
                            element={
                              <SubscriptionRoute page="notifications" requiredSubscription="premium">
                                <BroadcastComposer />
                              </SubscriptionRoute>
                            }
                          />
                          <Route
                            path="/notifications/sent"
                            element={
                              <SubscriptionRoute page="notifications" requiredSubscription="premium">
                                <SentNotifications />
                              </SubscriptionRoute>
                            }
                          />
                          <Route path="/settings" element={<AdminSettings />} />
                          <Route path="/faq" element={<FAQ />} />

                          {/* Fusionnées dans /dashboard (page unifiée à 5 onglets) — voir CLAUDE.md */}
                          <Route path="/dashboard-premium" element={<Navigate to="/dashboard" replace />} />
                          <Route path="/reports" element={<Navigate to="/dashboard" replace />} />

                          {/* Routes protégées par abonnement — Premium uniquement */}
                          <Route
                            path="/requests"
                            element={
                              <SubscriptionRoute page="requests" requiredSubscription="premium">
                                <RequestsManagementPremium />
                              </SubscriptionRoute>
                            }
                          />
                          <Route
                            path="/incidents"
                            element={
                              <SubscriptionRoute page="incidents" requiredSubscription="premium">
                                <IncidentsManagementPremium />
                              </SubscriptionRoute>
                            }
                          />

                          {/* Simulateur GPS — développement uniquement */}
                          {IS_DEV_MODE && <Route path="/dev/simulator" element={<DevSimulator />} />}

                          {/* Route de fallback */}
                          <Route path="*" element={<Navigate to="/admin/tracking" replace />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </MaybeDevProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
